// =============================================================================
// 島の椰子(結合メッシュ)を「個別オブジェクト」に分割する。連結成分+近接で1本ずつにまとめ、
// その頂点を取り出して個別Meshを生成し、元メッシュと同じ親・同じローカル変換で配置する
// （島の×20スケール/回転をそのまま継承＝見た目を完全に保つ）。元の結合メッシュは隠す。
// 採取=狙った1本をシーンから除去（畳むハック廃止・スパイクなし）。clone で増やせる。
// 岩は rocks.glb 由来ジオメトリを島上に個別配置。海面シェーダには触れない。
// =============================================================================
import { harvestTHREE } from './mesh3d';
import { findIsland, SEA_LEVEL } from './world';
import { groundHeight } from './terrain';
import type { Heightfield, NodeDef, ResourceNode } from './types';

const DEF: Record<string, NodeDef> = {
  // 椰子は木材と一緒にココナッツ(食料/水分)が採れる — これが唯一の食料源なので必須
  wood:  { item: 'wood',  icon: '🌴', label: '椰子', yield: 3, reach: 8.0, extra: { item: 'coconut', count: 1 } },
  fiber: { item: 'fiber', icon: '🌿', label: '草',   yield: 2, reach: 6.0 },
  stone: { item: 'stone', icon: '🪨', label: '岩',   yield: 3, reach: 6.0 },
};

// 椰子の材質グループ: 1本にまとまる近接半径と産出。Areca(低い葉物)=草=繊維、他=木材。
// 見た目に合わせた割り当て: 背の高い椰子=Palmeのみ木材。低く広がるCat/Tropical/Arecaは草(繊維)。
const PALM_GROUPS: { mats: string[]; type: 'wood' | 'fiber'; radius: number }[] = [
  { mats: ['Areca_Palm'],                      type: 'fiber', radius: 18 },
  { mats: ['Cat_Palm'],                        type: 'fiber', radius: 18 }, // 低い草
  { mats: ['Tropical_Palm'],                   type: 'fiber', radius: 20 }, // 低く広がる葉=草
  { mats: ['Palme', 'Palme_Blaetter'],         type: 'wood',  radius: 42 }, // 背の高い椰子の木（Rootは岩を椰子と誤認する原因のため除外）
];

export interface IslandResources {
  nodes: ResourceNode[];
  removeObject: (nodeId: number) => void;             // 採取しきった1本を除去
  // 視線レイ(原点o・方向d)が当たる資源オブジェクトのうち最も近いものを返す（オブジェクト全体の当たり判定）
  pick: (ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number) => ResourceNode | null;
  addPalm: (x: number, z: number, type?: 'wood' | 'fiber', y?: number) => ResourceNode | null;
  addRock: (x: number, z: number, y?: number, scale?: number) => ResourceNode | null;
  // クローン島(島Groupの複製を変換配置したもの)の資源をノード登録する。
  // mapPoint = メイン島world座標→クローンworld座標 の変換(nullを返すとそのノードは登録しない)。登録数を返す。
  registerCloneNodes: (cloneGroup: any, mapPoint: (x: number, y: number, z: number) => [number, number, number] | null) => number;
  aabbOf: (id: number) => number[][] | null; // デバッグ用: ノードの当たり判定ボックス群
}

let T: any = null;

interface Frag { mesh: any; idxs: number[]; wx: number; wz: number; minY: number; maxY: number; }

// 連結成分（頂点weld + union-find）→ 成分ごとの頂点index集合と world基部。
function meshFragments(mesh: any): Frag[] {
  const g = mesh.geometry, posA = g.attributes.position, n = posA.count;
  const map = new Map<string, number>(); const canon = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    const key = Math.round(posA.getX(i) * 1e3) + ',' + Math.round(posA.getY(i) * 1e3) + ',' + Math.round(posA.getZ(i) * 1e3);
    let id = map.get(key); if (id === undefined) { id = i; map.set(key, i); }
    canon[i] = id;
  }
  const parent = new Int32Array(n); for (let i = 0; i < n; i++) parent[i] = i;
  const find = (a: number): number => { while (parent[a] !== a) { parent[a] = parent[parent[a]]; a = parent[a]; } return a; };
  const uni = (a: number, b: number) => { a = find(canon[a]); b = find(canon[b]); if (a !== b) parent[a] = b; };
  const idx = g.index; const tn = idx ? idx.count : n;
  for (let t = 0; t + 2 < tn; t += 3) {
    const a = idx ? idx.getX(t) : t, b = idx ? idx.getX(t + 1) : t + 1, c = idx ? idx.getX(t + 2) : t + 2;
    uni(a, b); uni(b, c);
  }
  const e = mesh.matrixWorld.elements;
  const groups = new Map<number, Frag>();
  for (let i = 0; i < n; i++) {
    const r = find(canon[i]);
    let f = groups.get(r);
    if (!f) { f = { mesh, idxs: [], wx: 0, wz: 0, minY: Infinity, maxY: -Infinity }; groups.set(r, f); }
    f.idxs.push(i);
    const x = posA.getX(i), y = posA.getY(i), z = posA.getZ(i);
    const wx = e[0] * x + e[4] * y + e[8] * z + e[12];
    const wy = e[1] * x + e[5] * y + e[9] * z + e[13];
    const wz = e[2] * x + e[6] * y + e[10] * z + e[14];
    f.wx += wx; f.wz += wz; if (wy < f.minY) f.minY = wy; if (wy > f.maxY) f.maxY = wy;
  }
  const out: Frag[] = [];
  for (const f of groups.values()) { if (f.idxs.length >= 3) { f.wx /= f.idxs.length; f.wz /= f.idxs.length; out.push(f); } }
  return out;
}

interface Tree { frags: Frag[]; wx: number; wz: number; minY: number; maxY: number; }
function groupToTrees(frags: Frag[], radius: number): Tree[] {
  const trees: Tree[] = []; const r2 = radius * radius;
  for (const f of frags) {
    let best: Tree | null = null, bd = r2;
    for (const t of trees) { const dx = t.wx - f.wx, dz = t.wz - f.wz; const d = dx * dx + dz * dz; if (d < bd) { bd = d; best = t; } }
    if (best) {
      best.frags.push(f); const k = best.frags.length;
      best.wx = (best.wx * (k - 1) + f.wx) / k; best.wz = (best.wz * (k - 1) + f.wz) / k;
      if (f.minY < best.minY) best.minY = f.minY; if (f.maxY > best.maxY) best.maxY = f.maxY;
    } else trees.push({ frags: [f], wx: f.wx, wz: f.wz, minY: f.minY, maxY: f.maxY });
  }
  return trees;
}

// 1メッシュ内の頂点部分集合から sub-geometry を作り、元メッシュと同じ親・同じローカル変換で
// 配置した個別Meshを返す（島の変換を継承＝見た目同一）。元メッシュは別途 visible=false で隠す。
function subMesh(src: any, idxs: number[]): any {
  const g = src.geometry, posA = g.attributes.position, norA = g.attributes.normal, uvA = g.attributes.uv;
  const keep = new Set(idxs); const remap = new Map<number, number>();
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], ind: number[] = [];
  const addV = (oi: number): number => {
    let ni = remap.get(oi); if (ni !== undefined) return ni;
    ni = pos.length / 3; remap.set(oi, ni);
    pos.push(posA.getX(oi), posA.getY(oi), posA.getZ(oi));
    if (norA) nor.push(norA.getX(oi), norA.getY(oi), norA.getZ(oi));
    if (uvA) uv.push(uvA.getX(oi), uvA.getY(oi));
    return ni;
  };
  const idx = g.index; const tn = idx ? idx.count : posA.count;
  for (let t = 0; t + 2 < tn; t += 3) {
    const a = idx ? idx.getX(t) : t, b = idx ? idx.getX(t + 1) : t + 1, c = idx ? idx.getX(t + 2) : t + 2;
    if (keep.has(a) && keep.has(b) && keep.has(c)) ind.push(addV(a), addV(b), addV(c));
  }
  const ng = new T.BufferGeometry();
  ng.setAttribute('position', new T.BufferAttribute(new Float32Array(pos), 3));
  if (nor.length) ng.setAttribute('normal', new T.BufferAttribute(new Float32Array(nor), 3));
  if (uv.length) ng.setAttribute('uv', new T.BufferAttribute(new Float32Array(uv), 2));
  ng.setIndex(ind);
  if (!nor.length) ng.computeVertexNormals();
  const m = new T.Mesh(ng, src.material);
  m.position.copy(src.position); m.quaternion.copy(src.quaternion); m.scale.copy(src.scale);
  m.castShadow = m.receiveShadow = true;
  src.parent.add(m); // ★元メッシュと同じ親に付ける＝島の×20変換を継承し見た目が一致
  return m;
}

function findRockPrefabs(scene: any): { geometry: any; material: any }[] {
  const out: { geometry: any; material: any }[] = [];
  scene.traverse((o: any) => {
    if (!o.isInstancedMesh || !o.instanceMatrix) return;
    const a = o.instanceMatrix.array; if (a.length < 16) return;
    const sx = Math.hypot(a[0], a[1], a[2]);
    if (sx >= 50 && sx <= 220) out.push({ geometry: o.geometry, material: o.material });
  });
  return out;
}
const geomSize = (geo: any): number => {
  if (geo.computeBoundingBox) geo.computeBoundingBox();
  const b = geo.boundingBox; if (!b) return 1;
  return Math.max(b.max.x - b.min.x, b.max.y - b.min.y, b.max.z - b.min.z) || 1;
};

// オブジェクト(複数Mesh)の world AABB を [minX,minY,minZ,maxX,maxY,maxZ] で返す。
function worldAABB(meshes: any[]): number[] {
  let a0 = Infinity, b0 = Infinity, c0 = Infinity, a1 = -Infinity, b1 = -Infinity, c1 = -Infinity;
  for (const m of meshes) {
    const g = m.geometry; if (!g.boundingBox && g.computeBoundingBox) g.computeBoundingBox();
    const bb = g.boundingBox; if (!bb) continue;
    if (m.updateWorldMatrix) m.updateWorldMatrix(true, false);
    const e = m.matrixWorld.elements;
    for (let i = 0; i < 8; i++) {
      const x = (i & 1) ? bb.max.x : bb.min.x, y = (i & 2) ? bb.max.y : bb.min.y, z = (i & 4) ? bb.max.z : bb.min.z;
      const wx = e[0] * x + e[4] * y + e[8] * z + e[12];
      const wy = e[1] * x + e[5] * y + e[9] * z + e[13];
      const wz = e[2] * x + e[6] * y + e[10] * z + e[14];
      if (wx < a0) a0 = wx; if (wx > a1) a1 = wx; if (wy < b0) b0 = wy; if (wy > b1) b1 = wy; if (wz < c0) c0 = wz; if (wz > c1) c1 = wz;
    }
  }
  return [a0, b0, c0, a1, b1, c1];
}
// 巨大メッシュ用の分割ヒットボックス: 三角形を重心のXZセル(48world≈2.4m)へ振り分け、
// セルごとの頂点AABBを返す。岩場は溶接済み一枚岩(実測775×484)で、結合AABBだと目線が
// 箱の内側に入り「どこを見ても岩」になるため、当たり判定だけ実形状に沿わせる。
function cellBoxes(meshes: any[], cell = 48): number[][] {
  const boxes = new Map<string, number[]>();
  for (const m of meshes) {
    const g = m.geometry, pa = g.attributes.position, idx = g.index ? g.index.array : null;
    if (m.updateWorldMatrix) m.updateWorldMatrix(true, false);
    const e = m.matrixWorld.elements;
    const n = idx ? idx.length : pa.count;
    const w = (i: number) => {
      const x = pa.getX(i), y = pa.getY(i), z = pa.getZ(i);
      return [e[0] * x + e[4] * y + e[8] * z + e[12], e[1] * x + e[5] * y + e[9] * z + e[13], e[2] * x + e[6] * y + e[10] * z + e[14]];
    };
    for (let t = 0; t + 2 < n; t += 3) {
      const p0 = w(idx ? idx[t] : t), p1 = w(idx ? idx[t + 1] : t + 1), p2 = w(idx ? idx[t + 2] : t + 2);
      const key = Math.floor((p0[0] + p1[0] + p2[0]) / 3 / cell) + ',' + Math.floor((p0[2] + p1[2] + p2[2]) / 3 / cell);
      let bb = boxes.get(key);
      if (!bb) { bb = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity]; boxes.set(key, bb); }
      for (const p of [p0, p1, p2]) {
        if (p[0] < bb[0]) bb[0] = p[0]; if (p[1] < bb[1]) bb[1] = p[1]; if (p[2] < bb[2]) bb[2] = p[2];
        if (p[0] > bb[3]) bb[3] = p[0]; if (p[1] > bb[4]) bb[4] = p[1]; if (p[2] > bb[5]) bb[5] = p[2];
      }
    }
  }
  return [...boxes.values()];
}
// ノードの当たり判定ボックス群: 通常は結合AABB1個、巨大(4m超)なら分割セル箱。
function hitBoxes(meshes: any[]): number[][] {
  const a = worldAABB(meshes);
  return (a[3] - a[0] > 80 || a[5] - a[2] > 80) ? cellBoxes(meshes) : [a];
}
// 視線レイ(原点o・方向d)と AABB の交差: 入射距離(>=0)、当たらなければ-1。スラブ法。
function rayAABB(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, a: number[]): number {
  const o = [ox, oy, oz], d = [dx, dy, dz]; let t0 = 0, t1 = Infinity;
  for (let i = 0; i < 3; i++) {
    if (Math.abs(d[i]) < 1e-9) { if (o[i] < a[i] || o[i] > a[i + 3]) return -1; }
    else { let tn = (a[i] - o[i]) / d[i], tf = (a[i + 3] - o[i]) / d[i]; if (tn > tf) { const t = tn; tn = tf; tf = t; } if (tn > t0) t0 = tn; if (tf < t1) t1 = tf; if (t0 > t1) return -1; }
  }
  return t0;
}

export function buildIslandResources(scene: any, hf: Heightfield | null): IslandResources | null {
  T = harvestTHREE(scene);
  if (!T) return null;
  const island = findIsland(scene);
  if (!island) return null;
  island.updateWorldMatrix(true, true);

  const nodes: ResourceNode[] = [];
  const objs = new Map<number, any[]>();   // nodeId -> 個別Mesh群（除去対象）
  const aabbOf = new Map<number, number[][]>(); // nodeId -> 当たり判定ボックス群(巨大岩場はセル分割)
  let nid = 0;

  const mkNode = (type: 'wood' | 'fiber' | 'stone', x: number, y: number, z: number, meshes: any[]): ResourceNode => {
    const node: ResourceNode = {
      id: nid++, type: type === 'fiber' ? 'bush' : type === 'stone' ? 'rock' : 'palm',
      x, y, z, amount: DEF[type].yield, def: DEF[type],
    };
    nodes.push(node); objs.set(node.id, meshes); aabbOf.set(node.id, hitBoxes(meshes));
    return node;
  };

  const meshByMat = new Map<string, any>();
  island.traverse((o: any) => {
    if (!o.isMesh) return;
    const mat = Array.isArray(o.material) ? (o.material[0] && o.material[0].name) : (o.material && o.material.name);
    if (mat) meshByMat.set(mat, o);
  });

  // --- 椰子/草: 1本ずつ個別Mesh化 ---
  const palmPrefab: Record<string, any[]> = {}; // type -> 代表メッシュ群(増やす時の複製元。幹+葉など複数可)
  const palmPrefabBase: Record<string, { x: number; y: number; z: number }> = {}; // 代表木の基部world座標(クローン移動の基準)
  const hiddenMerged: any[] = [];
  for (const grp of PALM_GROUPS) {
    const frags: Frag[] = [];
    for (const mat of grp.mats) { const mesh = meshByMat.get(mat); if (mesh) { for (const f of meshFragments(mesh)) frags.push(f); if (!hiddenMerged.includes(mesh)) hiddenMerged.push(mesh); } }
    if (!frags.length) continue;
    for (const tree of groupToTrees(frags, grp.radius)) {
      if (tree.maxY - tree.minY < 1.0) continue; // ノイズ除去
      const byMesh = new Map<any, number[]>();
      for (const f of tree.frags) { const arr = byMesh.get(f.mesh) || []; for (const i of f.idxs) arr.push(i); byMesh.set(f.mesh, arr); }
      const meshes: any[] = [];
      for (const [src, idxs] of byMesh) meshes.push(subMesh(src, idxs));
      const gy = hf ? groundHeight(hf, tree.wx, tree.wz) : tree.minY;
      const baseY = (typeof gy === 'number' && gy > SEA_LEVEL) ? gy : tree.minY;
      mkNode(grp.type, tree.wx, baseY, tree.wz, meshes);
      if (!palmPrefab[grp.type] && meshes.length >= 1) { palmPrefab[grp.type] = meshes; palmPrefabBase[grp.type] = { x: tree.wx, y: baseY, z: tree.wz }; }
    }
  }
  for (const m of hiddenMerged) m.visible = false; // 元の結合椰子を隠す

  // --- 岩: 島の元アセット(結合された岩メッシュ RocksBig/RocksSmall)を個別Mesh化して石ノードに。
  //     椰子/草と同方式: 連結成分で1個ずつに分け、採取で個体だけ消す。旧rocks.glbランダム配置は廃止。 ---
  const ROCK_GROUPS: { mats: string[]; radius: number }[] = [{ mats: ['RocksBig', 'RocksSmall'], radius: 20 }];
  const container = new T.Group(); container.name = 'game-island-rocks'; scene.add(container);
  for (const grp of ROCK_GROUPS) {
    const frags: Frag[] = [];
    for (const mat of grp.mats) { const mesh = meshByMat.get(mat); if (mesh) { for (const f of meshFragments(mesh)) frags.push(f); mesh.visible = false; } }
    if (!frags.length) continue;
    for (const rk of groupToTrees(frags, grp.radius)) {
      const byMesh = new Map<any, number[]>();
      for (const f of rk.frags) { const arr = byMesh.get(f.mesh) || []; for (const i of f.idxs) arr.push(i); byMesh.set(f.mesh, arr); }
      const meshes: any[] = [];
      for (const [src, idxs] of byMesh) meshes.push(subMesh(src, idxs));
      const gy = hf ? groundHeight(hf, rk.wx, rk.wz) : rk.minY;
      const baseY = (typeof gy === 'number' && gy > SEA_LEVEL) ? gy : rk.minY;
      mkNode('stone', rk.wx, baseY, rk.wz, meshes);
    }
  }

  // 採取時は「その位置の柱まるごと」を消す: 同種(椰子/草)で footprint 内のノードを全て除去。
  // 低ポリ椰子は幹と葉が別断片=別ノードになりがちなので、葉だけ空中に残るのを防ぐ。
  const FOOT: Record<string, number> = { palm: 26, bush: 18, rock: 0 };
  const removeObject = (nodeId: number) => {
    const target = nodes.find((n) => n.id === nodeId); if (!target) return;
    // 溶接一枚岩(セル分割=複数ヒットボックス)の岩はメッシュを消さない: 1ノード=岩場全体のため、
    // 採取しきりで数百world幅の岩場が丸ごと消えて島の見た目が崩壊する。削るだけにする。
    // (椰子は葉張りでセル分割されるが、1本=1ノードなので従来通り消す)
    if (target.type === 'rock' && (aabbOf.get(nodeId) || []).length > 1) { target.amount = 0; return; }
    const R = FOOT[target.type] ?? 0, R2 = R * R;
    for (const n of nodes) {
      if (n.id !== nodeId) {
        if (n.type !== target.type) continue;
        const dx = n.x - target.x, dz = n.z - target.z;
        if (dx * dx + dz * dz > R2) continue;
      }
      const ms = objs.get(n.id); if (ms) for (const m of ms) { if (m.parent) m.parent.remove(m); }
      n.amount = 0; // 巻き込んだノードも消費済みにして見た目とリソースを一致させる
    }
  };

  // y を指定すると main島hf以外(自前地形の山岳島など)にも置ける
  const addPalm = (x: number, z: number, type: 'wood' | 'fiber' = 'wood', y?: number): ResourceNode | null => {
    const prefab = palmPrefab[type]; if (!prefab || !prefab.length || !hf) return null;
    const gy = y ?? groundHeight(hf, x, z); if (!(typeof gy === 'number' && gy > SEA_LEVEL)) return null;
    const ms: any[] = [];
    for (const src of prefab) { // 幹+葉など複数メッシュをまとめて複製
      if (!src.clone) continue;
      const m = src.clone(); src.parent.add(m); m.visible = true;
      mkNodeMoveXYZ(m, src, x, gy, z, palmPrefabBase[type]);
      ms.push(m);
    }
    if (!ms.length) return null;
    return mkNode(type, x, gy, z, ms);
  };
  // 同一親内で src と同じ見た目の clone を平行移動し、「基準点 base」が world(x,y,z) に来るようにする。
  // subMesh は頂点が結合ジオメトリのローカル座標のまま(=メッシュ原点≠木の位置)なので、
  // 原点基準で動かすと「指定位置+代表木のオフセット」へズレる(幽霊ノード化)。基準は木の基部を渡す。
  // 親(島Group)には回転が入っているため、world差分は worldToLocal で親ローカルへ変換する(スカラー除算では回転分がズレる)。
  function mkNodeMoveXYZ(m: any, src: any, x: number, y: number, z: number, base?: { x: number; y: number; z: number }): void {
    const e = src.matrixWorld.elements;
    const bx = base ? base.x : e[12], by = base ? base.y : e[13], bz = base ? base.z : e[14]; // 基準world座標(省略時=src原点)
    const a = src.position.clone(); a.set(x, y, z); src.parent.worldToLocal(a);
    const b = src.position.clone(); b.set(bx, by, bz); src.parent.worldToLocal(b);
    m.position.set(src.position.x + (a.x - b.x), src.position.y + (a.y - b.y), src.position.z + (a.z - b.z));
  }
  const addRock = (x: number, z: number, y?: number, scale = 1): ResourceNode | null => {
    const pfs = findRockPrefabs(scene); if (!pfs.length || !hf) return null;
    const pf = pfs[(Math.abs(Math.round(x * 7 + z * 13))) % pfs.length]; // 位置から決定論で岩の種類を選ぶ
    const gy = y ?? groundHeight(hf, x, z); if (!(typeof gy === 'number' && gy > SEA_LEVEL)) return null;
    const sc = (3.5 * scale) / geomSize(pf.geometry);
    const m = new T.Mesh(pf.geometry, pf.material);
    m.position.set(x, gy, z); m.scale.set(sc, sc, sc);
    m.rotation.y = (x * 0.37 + z * 0.71) % (Math.PI * 2); // 向きも散らす
    m.castShadow = m.receiveShadow = true; container.add(m);
    return mkNode('stone', x, gy, z, [m]);
  };

  // 視線レイが当たるオブジェクトのうち最も近いものを返す（オブジェクト全体=AABBで判定）。
  const pick = (ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): ResourceNode | null => {
    let best: ResourceNode | null = null, bestT = maxDist;
    for (const n of nodes) {
      if (n.amount <= 0) continue;
      const boxes = aabbOf.get(n.id); if (!boxes) continue;
      for (const a of boxes) {
        const t = rayAABB(ox, oy, oz, dx, dy, dz, a);
        if (t >= 0 && t < bestT) { bestT = t; best = n; }
      }
    }
    return best;
  };

  // クローン島の資源登録: 島Groupとその複製は traversal 順が一致するので、
  // 元ノードの個別メッシュ→クローン側メッシュを対応付け、mapPoint で座標変換したノードを追加する。
  const registerCloneNodes = (cloneGroup: any, mapPoint: (x: number, y: number, z: number) => [number, number, number] | null): number => {
    const orig: any[] = [], copy: any[] = [];
    island.traverse((o: any) => orig.push(o));
    cloneGroup.updateWorldMatrix(true, true);
    cloneGroup.traverse((o: any) => copy.push(o));
    if (orig.length !== copy.length) return 0; // 構造不一致(想定外)なら登録しない
    const map = new Map<any, any>();
    for (let i = 0; i < orig.length; i++) map.set(orig[i], copy[i]);
    const baseNodes = nodes.filter((n) => objs.get(n.id)?.every((m: any) => map.has(m))); // 島内メッシュ由来のみ
    let added = 0;
    for (const n of baseNodes) {
      const ms = (objs.get(n.id) || []).map((m: any) => map.get(m)).filter(Boolean);
      if (!ms.length) continue;
      const pt = mapPoint(n.x, n.y, n.z);
      if (!pt) continue; // 水没など、登録対象外
      const [x, y, z] = pt;
      const nn: ResourceNode = { id: nid++, type: n.type, x, y, z, amount: n.def.yield, def: n.def };
      nodes.push(nn); objs.set(nn.id, ms); aabbOf.set(nn.id, hitBoxes(ms));
      added++;
    }
    return added;
  };

  return { nodes, removeObject, pick, addPalm, addRock, registerCloneNodes, aabbOf: (id: number) => aabbOf.get(id) || null };
}
